import { Flex, Modal } from 'antd';
import useModal from 'antd/es/modal/useModal';
import { Form, Table } from 'antd/lib';
import { find, map, replace, trim } from 'lodash';
import moment from 'moment';
import { read, utils } from 'xlsx';
import { banks } from '../../constants/bank';
import { useBulkCreateEmployee } from '../../utils/api-hooks/employee.hook';

export function EmployeeImportForm() {
  const [modal, contextHolder] = useModal();
  const [form] = Form.useForm<{ file: File }>();
  const { mutateAsync: bulkCreateEmployee, isLoading: isImporting } =
    useBulkCreateEmployee();

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = function (event) {
      const data = event.target!.result;

      // 讀成 workbook（可讀取多個 sheet）
      const workbook = read(data, { type: 'array' });

      // 假設只讀第一個 sheet
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      // 將 sheet 轉為 JSON
      const json = utils.sheet_to_json<{
        姓名: string;
        電話: string;
        出生年月日: string;
        身分證字號: string;
        入職時間: string;
        匯款銀行代碼: string;
        匯款銀行帳號: string;
        員工類型: string;
      }>(worksheet);

      const items = map(json, (item) => {
        return {
          name: item['姓名'],
          phone: replace(trim(item['電話']), /[^\d]/g, ''),
          birthday: moment(item['出生年月日']).utcOffset(8).startOf('day'),
          nationalIdNumber: item['身分證字號'],
          onBoardingTime: moment(item['入職時間']).utcOffset(8).startOf('day'),
          bankCode: item['匯款銀行代碼'],
          bankAccount: replace(trim(item['匯款銀行帳號']), /[^\d]/g, ''),
          type: item['員工類型'],
        };
      });

      modal.confirm({
        title: '匯入員工資料',
        width: '80%',
        content: (
          <Flex vertical gap={8} align="stretch">
            <p>以下是匯入的資料，請確認無誤後提交。</p>
            <Table
              scroll={{ x: 'max-content' }}
              size="small"
              dataSource={items}
              pagination={false}
              columns={[
                {
                  title: '姓名',
                  dataIndex: 'name',
                  width: 100,
                },
                {
                  title: '電話',
                  dataIndex: 'phone',
                  width: 120,
                },
                {
                  title: '出生年月日',
                  dataIndex: 'birthday',
                  width: 120,
                  render: (date) => moment(date).format('YYYY-MM-DD'),
                },
                {
                  title: '身分證字號',
                  dataIndex: 'nationalIdNumber',
                  width: 120,
                },
                {
                  title: '入職時間',
                  dataIndex: 'onBoardingTime',
                  width: 120,
                  render: (date) => moment(date).format('YYYY-MM-DD'),
                },
                {
                  title: '匯款銀行',
                  dataIndex: 'bankCode',
                  width: 180,
                  render: (code) => {
                    const bank = find(banks, { bankCode: code });
                    return bank ? `${bank.bankCode} - ${bank.bankName}` : code;
                  },
                },
                {
                  title: '匯款銀行帳號',
                  dataIndex: 'bankAccount',
                  width: 150,
                },
                {
                  title: '員工類型',
                  dataIndex: 'type',
                  width: 100,
                },
              ]}
              rowKey={(record, index) =>
                `${record.name}-${record.nationalIdNumber}-${index}`
              }
            />
          </Flex>
        ),
        icon: null,
        okText: '確認匯入',
        cancelText: '取消',
        onOk: async () => {
          try {
            // 準備批量建立的資料，轉換成後端 API 需要的格式
            const bulkCreateData = {
              employees: items.map((item) => ({
                name: item.name,
                phone: item.phone,
                birthday: item.birthday.toISOString(),
                nationalIdNumber: item.nationalIdNumber,
                onBoardingTime: item.onBoardingTime.toISOString(),
                bankCode: item.bankCode,
                bankAccount: item.bankAccount,
                type: item.type,
              })),
            };

            // 執行批量建立員工
            await bulkCreateEmployee(bulkCreateData);

            // 成功後清除檔案選擇並關閉所有模態框
            e.target.value = '';
            Modal.destroyAll();
          } catch (error) {
            console.error('匯入失敗:', error);
            // 錯誤處理已經在 hook 中處理，這裡不需要額外處理
          }
          e.target.value = '';
        },
        onCancel() {
          e.target.value = ''; // 清除選擇的檔案
        },
      });
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <Flex vertical gap={8} align="start">
      {contextHolder}
      <a href="/員工資料匯入範本.xlsx">下載範本</a>
      <Form.Item label="請上傳員工資料" rules={[{ required: true }]}>
        <input
          type="file"
          accept=".xlsx"
          onChange={onFileChange}
          disabled={isImporting}
        />
      </Form.Item>
      <Form form={form} autoComplete="off" preserve={false}></Form>
    </Flex>
  );
}
